import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CommonButton from "../../../components/button/CommonButton";
import HomeHeader from "../../../components/header/HomeHeader";
import { imagePath } from "../../../constants/imagePath";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";
import { FaArrowLeft } from "react-icons/fa";
import FullscreenLoader from "../../../components/loader/fullScreenLoader/FullscreenLoader";
import "./OTPVerification.css";
import Card from "../../../components/card";
import { appApiHandler } from "../../../api/appApiHandler";
import { getErrorMessage } from "../../../constants/globalFunction";
import { string } from "../../../constants/string";

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
    }
  }, [email, navigate]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input if value is entered
    if (value && index < otp.length - 1) {
      setFocusedIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }

    // Clear error when user starts typing
    if (otpError) {
      setOtpError("");
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      setFocusedIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleFocus = (index) => {
    setFocusedIndex(index);
  };

  const validateOtp = () => {
    const otpString = otp.join("");
    if (!otpString || otpString.length !== 6) {
      setOtpError("Please enter 6-digit OTP");
      return false;
    }
    return true;
  };


  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateOtp()) return;

    const otpString = otp.join("");
    try {
      const reqData = {
        email: email,
        otp: otpString.trim(),
      };
      setButtonLoading(true);
      const response = await appApiHandler.verifyOtp(reqData);
      setButtonLoading(false);
      if (response?.status === 200) {
        navigate("/reset-password", { state: { email } });
      } else if (response?.message) {
        showErrorToast(getErrorMessage(response?.message));
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    } catch (err) {
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const onPressResend = async () => {
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setFocusedIndex(0);

    const reqData = {
      email: email,
    };
    setLoading(true);
    const response = await appApiHandler.sendOtp(reqData);
    setLoading(false);
    if (response?.status === 200) {
      console.log("OTP resent successfully");
    } else if (response?.message) {
      showErrorToast(getErrorMessage(response?.message));
    } else {
      showErrorToast(
        getErrorMessage(string.error.failed_due_to_internal_error)
      );
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="otp-verification-page" style={{ backgroundImage: `url(${imagePath.background})` }}>
      <HomeHeader />
      <div className="otp-verification-content">
        <div className="otp-verification-card">
          <img
            src={imagePath.appLogo}
            alt="Codeinfoway"
            className="otp-verification-logo"
          />

          <h1 className="otp-verification-title">OTP Verification</h1>
          <p className="otp-verification-subtext">
            Enter the 6 digits code that you received on your email
          </p>

          <form onSubmit={onSubmit}>
            {/* OTP Input Fields */}
            <div className="otp-verification-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onFocus={() => handleFocus(index)}
                  className={`otp-verification-input ${focusedIndex === index ? "focused" : ""
                    } ${otpError ? "error" : ""}`}
                  maxLength={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  disabled={loading}
                />
              ))}
            </div>

            {otpError && (
              <div className="otp-verification-error-text">{otpError}</div>
            )}

            <div className="otp-verification-resend-container">
              <span className="otp-verification-resend-text">
                Don't receive a code?{" "}
                <button
                  type="button"
                  onClick={onPressResend}
                  disabled={loading}
                  className="otp-verification-resend-button"
                >
                  Resend
                </button>
              </span>
            </div>

            <div className="button-row">
              <CommonButton
                title="Back"
                onPress={handleBack}
                outlined={true}
              />
              <CommonButton
                title={buttonLoading ? "Verifying..." : "Verify OTP"}
                type="submit"
                disabled={buttonLoading || loading}
              />
            </div>
          </form>

          {loading && <FullscreenLoader text="Resending OTP..." />}
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
